import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { TOKENS } from '../../../environments/environment';

declare global {
  interface Window {
    _slaaskSettings?: {
      key: string;
    };
    _slaaskLoader?: {
      load?: () => void;
      reload?: () => void;
    };
    _slaask?: {
      show?: (...args: unknown[]) => unknown;
    };
  }
}

@Injectable({
  providedIn: 'root'
})
export class SlaaskChatService {
  private initialized = false;
  private scriptLoadPromise: Promise<void> | null = null;
  private readonly scriptId = 'slaask-chat-loader';
  private readonly scriptSrc = 'https://cdn.slaask.com/chat_loader.js';
  private readonly chatReadyTimeoutMs = 5000;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  init(): void {
    void this.initAsync();
  }

  initAsync(): Promise<void> {
    if (this.initialized) {
      return Promise.resolve();
    }
    if (!isPlatformBrowser(this.platformId)) {
      return Promise.resolve();
    }
    if (!TOKENS.SLAASK_CHAT_KEY) {
      return Promise.resolve();
    }
    if (this.scriptLoadPromise !== null) {
      return this.scriptLoadPromise;
    }

    (globalThis as unknown as Window)._slaaskSettings = { key: TOKENS.SLAASK_CHAT_KEY };

    const existing = this.document.getElementById(this.scriptId) as HTMLScriptElement | null;
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        this.initialized = true;
        this.scriptLoadPromise = Promise.resolve();
        return this.scriptLoadPromise;
      }

      this.scriptLoadPromise = new Promise<void>((resolve, reject) => {
        existing.addEventListener('load', () => {
          existing.dataset.loaded = 'true';
          this.initialized = true;
          resolve();
        }, { once: true });
        existing.addEventListener('error', () => {
          this.scriptLoadPromise = null;
          reject(new Error('No se pudo cargar Slaask chat_loader.js'));
        }, { once: true });
      });
      return this.scriptLoadPromise;
    }

    this.scriptLoadPromise = new Promise<void>((resolve, reject) => {
      const script = this.document.createElement('script');
      script.id = this.scriptId;
      script.async = true;
      script.src = this.scriptSrc;
      script.onload = () => {
        script.dataset.loaded = 'true';
        this.initialized = true;
        resolve();
      };
      script.onerror = () => {
        this.scriptLoadPromise = null;
        reject(new Error('No se pudo cargar Slaask chat_loader.js'));
      };
      this.document.body.appendChild(script);
    });

    return this.scriptLoadPromise;
  }

  async open(): Promise<void> {
    await this.initAsync();
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const w = globalThis as unknown as Window;
    try {
      w._slaaskLoader?.load?.();
    } catch (err) {
      console.error('[SlaaskChatService] Error al cargar el chat:', err);
    }

    const chat = await this.waitForChatApp();
    if (!chat) {
      return;
    }

    try {
      chat.show?.();
    } catch (err) {
      console.error('[SlaaskChatService] Error al abrir el chat:', err);
    }
  }

  private async waitForChatApp(timeoutMs = this.chatReadyTimeoutMs): Promise<NonNullable<Window['_slaask']> | null> {
    const deadline = Date.now() + timeoutMs;

    while (Date.now() <= deadline) {
      const chat = (globalThis as unknown as Window)._slaask;
      if (chat && typeof chat.show === 'function') {
        return chat;
      }

      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    return null;
  }
}
